import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useState } from "react";

import { playerCardIdentityDisplay } from "@bw/shared/components";

import type { TournamentSanctionRatingsPreview } from "../api";
import { errorMessage, formatDelta, formatRating } from "../sanctionDisplay";

const COLLAPSED_ROWS = 8;

/**
 * Name plus #last4 handle, as elsewhere. An unresolved or withheld name
 * (private minor, missing profile) shows a dash and no handle: never the ID.
 */
function PlayerIdentity({
  userId,
  displayName,
}: {
  userId: string;
  displayName: string | null | undefined;
}) {
  const identity = playerCardIdentityDisplay(displayName ?? "", userId);
  return (
    <Typography
      component="span"
      noWrap
      sx={{ display: "block", fontSize: 12.5, fontWeight: 600 }}
    >
      {identity.name}
      {identity.handle ? (
        <Typography
          component="span"
          color="text.secondary"
          sx={{ fontSize: 12, fontWeight: 400, ml: 0.5 }}
        >
          {identity.handle}
        </Typography>
      ) : null}
    </Typography>
  );
}

export function RatingsPreviewTable({
  preview,
  isLoading,
  error,
}: {
  preview: TournamentSanctionRatingsPreview | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  const [expanded, setExpanded] = useState(false);
  if (isLoading) {
    return (
      <Box role="status" aria-label="Loading rating preview">
        <Skeleton height={22} />
        <Skeleton height={22} />
        <Skeleton height={22} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="warning" sx={{ py: 0, fontSize: 12.5 }}>
        {errorMessage(error)}
      </Alert>
    );
  }
  if (!preview) return null;

  const rows = expanded ? preview.rows : preview.rows.slice(0, COLLAPSED_ROWS);
  return (
    <Box>
      <Typography color="text.secondary" sx={{ fontSize: 12.5, mb: 0.5 }}>
        {preview.rows.length} rated of {preview.entrant_count} entrants ·{" "}
        {preview.match_count} {preview.match_count === 1 ? "match" : "matches"}{" "}
        · Glicko-2
      </Typography>
      <Table
        size="small"
        aria-label="Rating preview"
        sx={{ "& td, & th": { px: 0.75, fontSize: 12.5 } }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Player</TableCell>
            <TableCell align="right">Rating</TableCell>
            <TableCell align="right">Change</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.user_id}>
              <TableCell sx={{ maxWidth: 180 }}>
                <PlayerIdentity
                  userId={row.user_id}
                  displayName={row.display_name}
                />
                <Typography
                  component="span"
                  color="text.secondary"
                  sx={{ fontSize: 11.5, display: "block" }}
                >
                  {row.matches_counted}{" "}
                  {row.matches_counted === 1 ? "match" : "matches"}
                </Typography>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatRating(row.rating_before)}
                <ArrowForwardIcon
                  aria-label="to"
                  sx={{ fontSize: 13, mx: 0.5, verticalAlign: "-2px" }}
                />
                {formatRating(row.rating_after)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                  color:
                    row.delta > 0
                      ? "success.main"
                      : row.delta < 0
                        ? "error.main"
                        : "text.secondary",
                }}
              >
                {formatDelta(row.delta)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {preview.rows.length > COLLAPSED_ROWS ? (
        <Button
          size="small"
          onClick={() => setExpanded((value) => !value)}
          sx={{ mt: 0.5 }}
        >
          {expanded ? "Show fewer" : `Show all ${preview.rows.length}`}
        </Button>
      ) : null}
    </Box>
  );
}
